"use client";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";

function useInView(ref: React.RefObject<HTMLElement | null>, threshold = 0.15) {
 const [visible, setVisible] = useState(false);
 useEffect(() => {
 const el = ref.current;
 if (!el) return;
 const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
 obs.observe(el);
 return () => obs.disconnect();
 }, [ref, threshold]);
 return visible;
}

function RevealBlock({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
 const ref = useRef<HTMLDivElement>(null);
 const visible = useInView(ref);
 return (
 <div
 ref={ref}
 className="transition-all duration-[900ms] ease-out"
 style={{
 opacity: visible ? 1 : 0,
 transform: visible ? "translateY(0)" : "translateY(24px)",
 transitionDelay: `${delay}ms`,
 }}
 >
 {children}
 </div>
 );
}

export default function LetterFromTheFounders() {
 const [sealOpen, setSealOpen] = useState(false);
 const [spinKey, setSpinKey] = useState(0);

 const handleSealClick = () => {
 setSpinKey((k) => k + 1);
 setSealOpen((prev) => !prev);
 };

 return (
 <>
 {/* Envelope seal - click to toggle */}
 <div className="flex justify-center mb-12">
 <button
 onClick={handleSealClick}
 className="group relative focus:outline-none"
 aria-label={sealOpen ? "Close letter" : "Open letter"}
 >
 <div
 key={spinKey}
 className="w-20 h-20 rounded-full flex items-center justify-center animate-[spin360_700ms_ease-in-out_forwards]"
 style={{
 background: sealOpen
 ? "rgba(94,229,197,0.15)"
 : "rgba(94,229,197,0.08)",
 border: sealOpen
 ? "1px solid rgba(94,229,197,0.3)"
 : "1px solid rgba(94,229,197,0.15)",
 }}
 >
 <Image
 src="/yard-chair.png"
 alt=""
 width={40}
 height={40}
 className="object-contain transition-opacity duration-700"
 style={{ opacity: sealOpen ? 1 : 0.5 }}
 />
 </div>
 {!sealOpen && (
 <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-white/25 tracking-widest uppercase whitespace-nowrap group-hover:text-white/55 transition-colors">
 tap to open
 </span>
 )}
 </button>
 </div>

 <style jsx>{`
 @keyframes spin360 {
 from { transform: rotate(0deg); }
 to { transform: rotate(360deg); }
 }
 `}</style>

 {/* The letter image */}
 <div
 className="relative rounded-2xl overflow-hidden transition-all duration-1000"
 style={{
 opacity: sealOpen ? 1 : 0.3,
 filter: sealOpen ? "blur(0)" : "blur(4px)",
 transform: sealOpen ? "scale(1)" : "scale(0.97)",
 }}
 >
 <Image
 src="/founders-letter.png"
 alt="A letter from the founders of The Yard"
 width={800}
 height={1000}
 className="w-full h-auto rounded-2xl"
 priority
 />
 </div>

 {/* Post-letter CTA */}
 <div className="mt-16 text-center">
 <RevealBlock>
 <p className="text-white/45 text-sm mb-6" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic" }}>
 Ready to take your seat?
 </p>
 <a
 href="/get-in"
 className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full text-sm font-medium text-white/70 hover:text-white transition-all duration-300"
 style={{
 background: "rgba(255,255,255,0.04)",
 backdropFilter: "blur(12px)",
 border: "1px solid rgba(255,255,255,0.08)",
 }}
 >
 Join The Yard
 </a>
 </RevealBlock>
 </div>
 </>
 );
}
